import { useEffect, useState, useRef } from 'preact/hooks';

import * as waveAnimation from './utils/wave-animation-calculations';
import Waves from './components/waves/waves';
import Timer from './components/timer/timer';
import Wind from './components/wind/wind';
import SurfscaleImperial from './static/surfscale-imperial.svg';
import SurfscaleMetric from './static/surfscale-metric.svg';

import { iconColor } from 'components/swell-icon/swell-icon';
import Units from 'utils/units';

const dict = {
  SurfscaleImperial,
  SurfscaleMetric,
};

const App = props => {
  const css = 'swell-preview';
  const [wavesList, updateWavesList] = useState([]);
  const [newWaveGenerated, setNewWaveGenerated] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const units = Units.current.length;
  const wavesContainer = useRef();
  const [waveAmp, setWaveAmp] = useState([]);

  let waveQuantity = 0;

  const setWindLabelStyle = {
    background: props.windData.offshoreness.color,
    color: props.windData.offshoreness.text === 'on' ? 'white' : 'black',
  };

  const generateFirstWaveView = data => {
    for (let i = 0; i < data.length; i++) {
      if (data[i] !== null) {
        setNewWaveProperties(data[i], i, true);
      }
    }
  };

  const generateWaveIntervals = data => {
    const intervals = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i] !== null) {
        intervals.unshift(
          setInterval(() => {
            setNewWaveProperties(data[i], i, false);
          }, data[i].period * 1000)
        );
      }
    }
    return intervals;
  };

  const setNewWaveProperties = (wave, index, firstRender) => {
    waveQuantity++;
    const waveId = waveQuantity;
    if (firstRender && wave.period <= 5) {
      const time =
        waveAnimation.calculateAnimationDuration(wave.period) / playbackSpeed;
      const spawnInterval = wave.period;
      const startingWaveQuantity = time / spawnInterval;
      const steps = 240 / startingWaveQuantity;
      for (let i = 0; i < startingWaveQuantity; i++) {
        updateWavesList(prevState => [
          ...prevState,
          {
            period: wave.period,
            height: wave.height,
            color: iconColor(wave.period, wave.height),
            swellNumber: index + 1,
            sheltered: wave.height < 0 ? true : false,
            index: waveId,
            startTime: new Date(),
            startX: Math.round(steps * i),
          },
        ]);
      }
    } else {
      updateWavesList(prevState => [
        ...prevState,
        {
          period: wave.period,
          height: wave.height,
          color: iconColor(wave.period, wave.height),
          swellNumber: index + 1,
          sheltered: wave.height < 0 ? true : false,
          index: waveId,
          startTime: new Date(),
          startX: 0,
        },
      ]);
    }
  };

  const addAmpRefs = wave => {
    let now = new Date();
    now.setSeconds(now.getSeconds());
    const nowTime = now.toLocaleTimeString().replace(/:/g, '');
    setWaveAmp(waveAmp => waveAmp.filter(wave => wave.end > nowTime));
    setWaveAmp(prevState => [...prevState, wave]);
  };

  useEffect(() => {
    generateFirstWaveView(props.swellData);
    const intervals = generateWaveIntervals(props.swellData);

    return () => {
      intervals.forEach(id => clearInterval(id));
    };
  }, []);

  return (
    <section className={`${css}`}>
      <div className={`${css}__title`}>
        <div className={`${css}__title-text`}>Open Ocean</div>
        <div style={setWindLabelStyle} className={`${css}__title-offshoreness`}>
          Wind: {props.windData.offshoreness.text}
        </div>
        <div className={`${css}__title-text`}>Near Shore</div>
      </div>
      <div className={`${css}__container`}>
        <div className={`${css}__scale-container`}>
          <img
            alt="Metric surfscale"
            className={`${css}__scale`}
            src={
              units === 'Metric'
                ? `${dict.SurfscaleMetric}`
                : `${dict.SurfscaleImperial}`
            }
            width="30"
            height="72"
          ></img>
        </div>
        <div
          className={`${css}__waves`}
          id={newWaveGenerated}
          ref={wavesContainer}
        >
          <div className={`${css}__waves-block`}>
            {props.windData.offshoreness.text === 'glass' ||
              (props.windData.speed > 0 && (
                <div className={`${css}__wind-block`}>
                  <div>
                    <Wind
                      windData={props.windData}
                      playbackSpeed={playbackSpeed}
                    />
                  </div>
                  <div>
                    <Wind
                      windData={props.windData}
                      playbackSpeed={playbackSpeed}
                    />
                  </div>
                </div>
              ))}

            {wavesList.map((wave, index) => (
              <Waves
                ampRefs={addAmpRefs}
                key={wave.waveId}
                wavesList={wavesList}
                waveType={wave.swellNumber}
                waveIndex={index}
                wavePeriod={wave.period}
                waveHeight={wave.height}
                waveColor={wave.color}
                waveLength={waveAnimation.calculateWaveLength(wave.period)}
                isWaveSheltered={wave.sheltered}
                waveStacking={Math.abs(wave.height)}
                startX={wave.startX}
                startTime={wave.startTime}
                playbackSpeed={playbackSpeed}
              />
            ))}
          </div>
        </div>
        <div class={`${css}__legend`}>
          <span className={`${css}__legend-timer`}>
            <Timer playbackSpeed={playbackSpeed} />
          </span>
          <span className={`${css}__legend-arrow`}></span>
          <span className={`${css}__legend-value`}>
            <span>{units === 'Metric' ? '500' : '1640'}</span>
            <span>{units === 'Metric' ? 'm' : 'ft'}</span>
          </span>
        </div>
      </div>
    </section>
  );
};

export default App;
